import { readRuntimeConfig } from '@/lib/api/runtime-config';
/**
 * CookieYes, the cookie-consent banner, and the rule that keeps it off clinical screens.
 *
 * The banner is a live third-party script that cannot be pinned with SRI (CookieYes regenerates
 * the file whenever the banner is edited), so it only ever runs on the public sign-in pages:
 * they call loadCookieYes(). Everything that shows clinical data renders through
 * ConsentScriptBoundary, which marks the document (after which the script is never injected
 * into it) and, if the script is already running here, reloads into a clean document before
 * rendering anything. The visitor's choice outlives the script: CookieYes keeps it in a
 * first-party cookie that consentState.ts reads on every screen.
 *
 * Nothing here does anything unless `__RUNTIME_CONFIG__.cookieyesSiteId` is set, which only
 * production does (COOKIEYES_SITE_ID). See docs/reference/cookie-consent.md.
 */
const SCRIPT_ID = 'cookieyes';
const SITE_ID = /^[0-9a-f]{32}$/;
/** Query flag asking a freshly loaded public page to open the preference centre on arrival. */
export const COOKIE_SETTINGS_PARAM = 'cookie-settings';
let protectedContentShown = false;
let openWhenLoaded = false;
let bannerLoadBound = false;
/** The configured site id, or null when consent is not managed (or the id is malformed). */
export function cookieYesSiteId() {
    const id = readRuntimeConfig().cookieyesSiteId ?? '';
    return SITE_ID.test(id) ? id : null;
}
/** Is cookie consent switched on for this deployment? */
export function isConsentManaged() {
    return cookieYesSiteId() !== null;
}
/** Has the banner script been injected into this document? */
export function isCookieYesLoaded() {
    return document.getElementById(SCRIPT_ID) !== null;
}
/**
 * Record that this document has rendered clinical data. From then on loadCookieYes() refuses to
 * inject the banner script here; a public page reached later in the same document (the sign-in
 * screen after a logout, which is a client-side navigation) gets it only by reloading.
 */
export function markProtectedContentShown() {
    protectedContentShown = true;
}
/**
 * Inject the banner script, once per document. Returns whether it is (now) present. Refuses when
 * consent is not managed or when this document has shown clinical data.
 */
export function loadCookieYes() {
    const siteId = cookieYesSiteId();
    if (!siteId || protectedContentShown)
        return false;
    if (isCookieYesLoaded())
        return true;
    bindBannerLoad();
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://cdn-cookieyes.com/client_data/${siteId}/script.js`;
    document.head.appendChild(script);
    return true;
}
/**
 * Open CookieYes's preference centre so the visitor can change or withdraw their choice. When
 * the script cannot run in this document (it has shown clinical data), load a fresh copy of the
 * current page, which opens the preference centre as soon as the banner arrives.
 */
export function openCookieSettings() {
    if (!isConsentManaged())
        return;
    const revisit = window.revisitCkyConsent;
    if (typeof revisit === 'function') {
        revisit();
        return;
    }
    // Injected but not arrived yet (or blocked by an extension): open it when it lands.
    if (loadCookieYes()) {
        openWhenLoaded = true;
        return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set(COOKIE_SETTINGS_PARAM, '1');
    window.location.assign(url.toString());
}
// cookieyes_banner_load is what the script actually dispatches (its docs say
// "cookieyes_banner_loaded"); it fires whether or not a banner is shown to this visitor.
function bindBannerLoad() {
    if (bannerLoadBound)
        return;
    bannerLoadBound = true;
    document.addEventListener('cookieyes_banner_load', () => {
        const url = new URL(window.location.href);
        const requested = url.searchParams.has(COOKIE_SETTINGS_PARAM);
        if (!openWhenLoaded && !requested)
            return;
        openWhenLoaded = false;
        if (requested) {
            url.searchParams.delete(COOKIE_SETTINGS_PARAM);
            window.history.replaceState(window.history.state, '', url.toString());
        }
        ;
        window.revisitCkyConsent?.();
    });
}
